import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { tokens } from "@/tokens";
import { useUpdateFirstSaleCaseMutation } from "@/services/first-sale";

// Auto-managed statuses (DRAFT, DOCUMENTS_PENDING, READY_FOR_VALIDATION)
// are set by syncCaseStatus based on portal/doc state — not shown here.
// Only statuses that require manual override are available.
const MANUAL_CASE_STATUSES = [
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

interface EditCaseModalProps {
  open: boolean;
  onClose: () => void;
  caseData: any;
  tenantId?: string;
}

export const EditCaseModal: React.FC<EditCaseModalProps> = ({
  open,
  onClose,
  caseData,
  tenantId,
}) => {
  const [form, setForm] = useState({
    poNumber: "",
    linkedPOs: "",
    middlemanName: "",
    factoryName: "",
    middlemanValue: "",
    factoryValue: "",
    dutyRate: "",
    htsCode: "",
    productDescription: "",
  });
  const [statusOverride, setStatusOverride] = useState("");

  const [updateCase, { isLoading }] = useUpdateFirstSaleCaseMutation();

  useEffect(() => {
    if (caseData && open) {
      setForm({
        poNumber: caseData.poNumber || "",
        linkedPOs: (caseData.linkedPOs || []).join(", "),
        middlemanName: caseData.middlemanName || "",
        factoryName: caseData.factoryName || "",
        middlemanValue: caseData.middlemanValue != null ? String(caseData.middlemanValue) : "",
        factoryValue: caseData.factoryValue != null ? String(caseData.factoryValue) : "",
        dutyRate: caseData.dutyRate != null ? String(caseData.dutyRate) : "",
        htsCode: caseData.htsCode || "",
        productDescription: caseData.productDescription || "",
      });
      setStatusOverride("");
    }
  }, [caseData, open]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.poNumber.trim()) return;

    try {
      const middlemanValue = form.middlemanValue ? parseFloat(form.middlemanValue) : undefined;
      const factoryValue = form.factoryValue ? parseFloat(form.factoryValue) : undefined;
      const dutyRate = form.dutyRate ? parseFloat(form.dutyRate) : undefined;
      const linkedPOs = form.linkedPOs
        .split(",")
        .map((po) => po.trim())
        .filter(Boolean);

      const input: Record<string, any> = {
        poNumber: form.poNumber.trim(),
        linkedPOs: linkedPOs.length > 0 ? linkedPOs : undefined,
        middlemanName: form.middlemanName.trim() || undefined,
        factoryName: form.factoryName.trim() || undefined,
        middlemanValue,
        factoryValue,
        dutyRate,
        htsCode: form.htsCode.trim() || undefined,
        productDescription: form.productDescription.trim() || undefined,
      };
      if (statusOverride) {
        input.status = statusOverride;
      }

      await updateCase({
        caseId: caseData._id,
        ...(tenantId && { tenantId }),
        input,
      }).unwrap();

      onClose();
    } catch (err) {
      console.error("Failed to update case:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontSize: tokens.fontSize.lg,
          fontWeight: tokens.fontWeight.heavy,
          color: tokens.text.primary,
        }}
      >
        Edit Case {caseData?.caseNumber}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="PO Number"
                value={form.poNumber}
                onChange={handleChange("poNumber")}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Linked POs"
                value={form.linkedPOs}
                onChange={handleChange("linkedPOs")}
                fullWidth
                placeholder="Comma-separated PO numbers"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Middleman Name"
                value={form.middlemanName}
                onChange={handleChange("middlemanName")}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Factory Name"
                value={form.factoryName}
                onChange={handleChange("factoryName")}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Middleman Value (USD)"
                value={form.middlemanValue}
                onChange={handleChange("middlemanValue")}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Factory Value (USD)"
                value={form.factoryValue}
                onChange={handleChange("factoryValue")}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Duty Rate (%)"
                value={form.dutyRate}
                onChange={handleChange("dutyRate")}
                fullWidth
                type="number"
                placeholder="e.g., 25"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="HTS Code"
                value={form.htsCode}
                onChange={handleChange("htsCode")}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Product Description"
                value={form.productDescription}
                onChange={handleChange("productDescription")}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Manual Status Override (optional)</InputLabel>
                <Select
                  value={statusOverride}
                  label="Manual Status Override (optional)"
                  onChange={(e) => setStatusOverride(e.target.value)}
                >
                  <MenuItem value="">
                    <em>No override — auto-managed</em>
                  </MenuItem>
                  {MANUAL_CASE_STATUSES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!form.poNumber.trim() || isLoading}
          sx={{
            bgcolor: tokens.brand.accent,
            textTransform: "none",
            "&:hover": { bgcolor: tokens.brand.accent, opacity: 0.9 },
          }}
        >
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCaseModal;
